/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { apiSlice } from "../apiSlice";
import { API, API_SLICE_TAGS } from "../../../globals/globals";
import {
  IEvent,
  IServiceDeviceData,
  IArgs,
  IPaginatedData,
  IInventory,
  IGROUPS,
  IDevicesToSiteGroup,
  IAllDevices,
  IRnCellArg,
  IRadioNodeCellConfig,
  IRnLteCellConfig,
  ICellArg,
  IdeviceCounts,
  IdrawerCounts,
  ISupersetTokenRequest,
  ISupersetTokenResponse,
  IReportPreviewResponse,
  IReportsResponse,
  ICellKpiArgs,
  NoteData,
  ILabelsToDevice,
  IDevicesToLabel,
  IThresholdCrossingArgs,
  IAlarmCount,
  ICellsCounts,
  IRadioNodesStats,
  IAlarmIncidentTracking,
  ITopOffenderResponse,
  IRemoteEquipment,
  IInventoryGraphResponse,
  INodesCounts,
  IRadioNodeInventory,
  ICellInventory,
  ICellsSummaryModel,
  IInventoryPagedResponse,
} from "./types";
import { sanitizeQueryParams } from "../../helpers/helpers";
import { FetchBaseQueryMeta } from "@reduxjs/toolkit/dist/query";
import { encode as base64Encode } from "base-64";
import { IDevicesToGroup, IProfilesToDevice } from "../admin/types";
import { IExternalPerformanceArgs, INrCell, IRadioNodes } from "../deviceConfig/types";

export const deviceApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNodes: builder.query<IPaginatedData<IServiceDeviceData>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.NODES.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.NODES],
      // transformResponse: (response: IServicesDevice[], meta, arg) => {
      //   // Nothing to do here yet - just leaving this as a placeholder
      //   return response;
      // },
    }),
    getNodesCounts: builder.query<INodesCounts, { geoRegionIds?: string[] } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        const geoRegionIds = args?.geoRegionIds || [];
        if (geoRegionIds.length > 0) {
          params.set("geoRegionIds", geoRegionIds.join(","));
        }
        const queryString = params.toString();
        return {
          url: `${API.ENDPOINTS.NODESCOUNTS.path}${queryString ? `?${queryString}` : ""}`,
        };
      },
      providesTags: [API_SLICE_TAGS.NODESCOUNTS],
    }),
    getAllDevices: builder.query<IAllDevices[], string>({
      query: (args) => ({
        url: `${API.ENDPOINTS.ALLDEVICES.path}/${args}`,
      }),
      providesTags: [API_SLICE_TAGS.ALLDEVICES],
    }),
    getAllDevicesByGroup: builder.query<IServiceDeviceData[], IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.ALLDEVICESBYGROUP.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.ALLDEVICESBYGROUP],
    }),
    getCellKpiDetails: builder.mutation<any[], ICellKpiArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.CELLKPIDATA.path}/${args?.nodeType}/dashboard/${args?.siteName}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
      })
    }),
    getAlarms: builder.query<IPaginatedData<IEvent>, IArgs>({
      query: (args) => ({
        url: args?.deviceKey
          ? `${API.ENDPOINTS.ALARMS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`
          : `${API.ENDPOINTS.ALARMS.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.ALARMS],
    }),
    getAlarmsByDeviceKey: builder.mutation<IPaginatedData<IEvent>, IArgs>({
      query: (args) => ({
        url: args?.deviceKey
          ? `${API.ENDPOINTS.ALARMS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`
          : `${API.ENDPOINTS.ALARMS.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
      }),
    }),
    getConditions: builder.query<IPaginatedData<IEvent>, IArgs>({
      query: (args) => ({
        url: args?.deviceKey
          ? `${API.ENDPOINTS.CONDITIONS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`
          : `${API.ENDPOINTS.CONDITIONS.path}?${sanitizeQueryParams(args.filters)}`
      }),
      providesTags: [API_SLICE_TAGS.CONDITIONS],
    }),
    getEvents: builder.query<IPaginatedData<IEvent>, IArgs>({
      query: (args) => ({
        url: args?.deviceKey
          ? `${API.ENDPOINTS.EVENTS.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`
          : `${API.ENDPOINTS.EVENTS.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.EVENTS],
    }),

    getSwVersions: builder.query<Array<string>, IArgs>({
      query: (args) => {
        // Ensure filters parameter is always present for this endpoint
        const queryParams = new URLSearchParams(args.filters);
        if (!queryParams.has('filters')) {
          const emptyFilter = { type: "AND", operator: "", fieldName: "", value: "", predicates: [] };
          queryParams.append('filters', base64Encode(JSON.stringify(emptyFilter)));
        }
        return {
          url: `${API.ENDPOINTS.SWVERSION.path}?${sanitizeQueryParams(queryParams.toString())}`
        };
      },
      providesTags: [API_SLICE_TAGS.SWVERSION],
    }),

    getInventory: builder.query<IPaginatedData<IInventory>, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.INVENTORY.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}` }),
      providesTags: [API_SLICE_TAGS.INVENTORY],
    }),

    getDasRadioNodes: builder.query<IRemoteEquipment[], IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.DAS_RADIONODES.path}?deviceKey=${args?.deviceKey}` }),
      providesTags: [API_SLICE_TAGS.DAS_RADIONODES],
    }),

    getDasInventory: builder.query<IRemoteEquipment[], IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.DAS_INVENTORY.path}?deviceKey=${args?.deviceKey}` }),
      providesTags: [API_SLICE_TAGS.DAS_INVENTORY],
    }),

    getDasInventoryGraph: builder.query<IInventoryGraphResponse, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.DAS_INVENTORY_GRAPH.path}?deviceKey=${args?.deviceKey}` }),
      providesTags: [API_SLICE_TAGS.DAS_INVENTORY_GRAPH],
    }),

    getSupportedDasModels: builder.query<string[], void>({
      query: () => ({ url: API.ENDPOINTS.DAS_SUPPORTED_MODELS.path }),
    }),

    getDasFeatureFlags: builder.query<{ enabled: boolean; inventoryDiscovery: boolean }, string>({
      query: (modelName) => ({ url: `${API.ENDPOINTS.DAS_FEATURE_FLAGS.path}/${encodeURIComponent(modelName)}` }),
    }),

    // Inventory Dashboard endpoints
    getInventoryRadioNodes: builder.query<IInventoryPagedResponse<IRadioNodeInventory>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.INVENTORY_RADIONODES.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.INVENTORY_RADIONODES],
    }),

    getInventoryCells: builder.query<IInventoryPagedResponse<ICellInventory>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.INVENTORY_CELLS.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.INVENTORY_CELLS],
    }),

    getCellsSummary: builder.query<IInventoryPagedResponse<ICellsSummaryModel>, { geoRegions?: string[]; search?: string; page?: number; size?: number }>({
      query: ({ geoRegions, search, page = 0, size = 1000 }) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(size));
        if (search) params.set('search', search);
        if (geoRegions && geoRegions.length > 0) {
          geoRegions.forEach((r) => params.append('geoRegion', r));
        }
        return { url: `${API.ENDPOINTS.INVENTORY_CELLS_SUMMARY.path}?${params.toString()}` };
      },
      providesTags: [API_SLICE_TAGS.INVENTORY_CELLS_SUMMARY],
    }),

    getInventoryRadioNodeModels: builder.query<string[], string[] | undefined>({
      query: (geoRegions) => {
        const params = new URLSearchParams();
        if (geoRegions && geoRegions.length > 0) {
          geoRegions.forEach(r => params.append('geoRegion', r));
        }
        const queryString = params.toString();
        const url = `${API.ENDPOINTS.INVENTORY_RADIONODE_MODELS.path}${queryString ? `?${queryString}` : ''}`;
        console.log('API: Fetching RadioNode models from:', url, 'with geoRegions:', geoRegions);
        return { url };
      },
      transformResponse: (response: any) => {
        console.log('API: RadioNode models raw response:', response, 'Type:', typeof response, 'isArray:', Array.isArray(response));
        // Ensure response is always an array
        if (Array.isArray(response)) {
          return response.filter((item: any) => item != null && item !== '');
        }
        console.warn('API: RadioNode models response is not an array, returning empty array');
        return [];
      },
      providesTags: [API_SLICE_TAGS.INVENTORY_RADIONODE_MODELS],
    }),

    getInventoryStatus: builder.query<string[], string>({
      query: (type) => ({
        url: `${API.ENDPOINTS.INVENTORY_STATUS.path}?type=${type}`,
      }),
      transformResponse: (response: any) => {
        console.log('API: Inventory status raw response:', response, 'Type:', typeof response, 'isArray:', Array.isArray(response));
        // Ensure response is always an array
        if (Array.isArray(response)) {
          return response.filter((item: any) => item != null && item !== '');
        }
        console.warn('API: Inventory status response is not an array, returning empty array');
        return [];
      },
      providesTags: [API_SLICE_TAGS.INVENTORY_STATUS],
    }),

    exportInventory: builder.mutation<Blob, { type: string; filters?: string; sort?: string; search?: string }>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args.filters) {
          params.append("filter", args.filters);
          params.append("isEncoded", "true");
        }
        if (args.sort) {
          params.append("sort", args.sort);
        }
        if (args.search) {
          params.append("search", args.search);
        }
        return {
          url: `${API.ENDPOINTS.INVENTORY_EXPORT.path}/${args.type}?${params.toString()}`,
          responseHandler: (response) => response.blob(),
        };
      },
    }),

    getGroups: builder.query<IGROUPS[], void>({
      query: () => ({ url: `${API.ENDPOINTS.GROUPS.path}` }),
      providesTags: [API_SLICE_TAGS.GROUPS],
    }),

    createGroup: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.GROUPSCREATE.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteGroup: builder.mutation({
      query: (groupKey) => ({
        url: `${API.ENDPOINTS.GROUPS.path}/${groupKey}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteDevice: builder.mutation({
      query: (deviceKey: any) => ({
        url: `${API.ENDPOINTS.REMOVEDEVICE.path}?snKey=${deviceKey}`,
        method: "DELETE",
        responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    addDevicesToGroup: builder.mutation({
      query: (payload: IDevicesToGroup) => ({
        url: `${API.ENDPOINTS.GROUPS.path}/${payload.groupKey}/add`,
        method: "POST",
        body: payload.deviceKeys,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    removeDevicesFromGroup: builder.mutation({
      query: (payload: IDevicesToGroup) => ({
        url: `${API.ENDPOINTS.GROUPS.path}/${payload.groupKey}/remove`,
        method: "POST",
        body: payload.deviceKeys,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getDeviceCounts: builder.query<IdeviceCounts, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.DEVICECOUNTS.path}/${args?.deviceKey}` }),
      providesTags: [API_SLICE_TAGS.DEVICECOUNTS],
    }),

    getRadioNodesStats: builder.query<IRadioNodesStats, string[] | undefined>({
      query: (geoRegions) => {
        const params = new URLSearchParams();
        if (geoRegions && geoRegions.length > 0) {
          params.set('geoRegion', geoRegions.join(','));
        }
        const queryString = params.toString();
        return { url: `${API.ENDPOINTS.RADIONODESSTATS.path}${queryString ? `?${queryString}` : ''}` };
      },
      providesTags: [API_SLICE_TAGS.RADIONODESSTATS],
    }),

    getDrawerCounts: builder.mutation<IdrawerCounts, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.DRAWERCOUNTS.path}/${args?.deviceKey}`,
        method: "GET",
      }),
    }),

    exportEvents: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.EXPORTEVENTS.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    exportAlarms: builder.mutation({
      query: (args: IArgs) => ({
        url: `${API.ENDPOINTS.EXPORTALARMS.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    createSiteGroup: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.SITEGROUPS.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteSiteGroup: builder.mutation({
      query: (groupKey) => ({
        url: `${API.ENDPOINTS.SITEGROUPS.path}/${groupKey}`,
        method: "DELETE",
        responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),
    addDevicesToSiteGroup: builder.mutation({
      query: (payload: IDevicesToSiteGroup) => ({
        url: `${API.ENDPOINTS.SITEGROUPS.path}/${payload.groupkey}/add`,
        method: "POST",
        body: payload.deivceKeys,
        responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),
    removeDevicesFromSiteGroup: builder.mutation({
      query: (payload: IDevicesToSiteGroup) => ({
        url: `${API.ENDPOINTS.SITEGROUPS.path}/${payload.groupkey}/remove`,
        method: "POST",
        body: payload.deivceKeys,
        responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),
    getRnCellConfig: builder.query<IRadioNodeCellConfig, IRnCellArg>({
      query: (args) => ({
        url: `${API.ENDPOINTS.RNCELLDETAILS.path}/${args?.modelKey}`,
        method: "GET",
      }),
    }),
    getRnLteCellConfig: builder.mutation<IRnLteCellConfig, ICellArg>({
      query: (args) => ({
        url: `${API.ENDPOINTS.RNCELLCONFIGS.path}/getLteCell/${args?.deviceKey}/${args?.radioNodeId}`,
        method: "GET",
      }),
    }),
    getRnNrCellConfig: builder.mutation<INrCell, ICellArg>({
      query: (args) => ({
        url: `${API.ENDPOINTS.RNCELLCONFIGS.path}/getNrCell/${args?.deviceKey}/${args?.radioNodeId}`,
        method: "GET",
      }),
    }),

    exportConditions: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.EXPORTCONDITIONS.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),

    exportDevices: builder.mutation({
      query: (geoRegion: string) => ({
        url: `${API.ENDPOINTS.EXPORTDEVICES.path}?geoRegion=${geoRegion}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    exportNodeDevices: builder.mutation({
      query: (args: IArgs) => ({
        url: `${API.ENDPOINTS.EXPORTDEVICES.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    getSupersetToken: builder.query<ISupersetTokenResponse, ISupersetTokenRequest>({
      query: (payload) => ({
        url: API.ENDPOINTS.SUPERSETTOKEN.path,
        method: "POST",
        body: payload,
      }),
    }),
    getLteNrCellRn: builder.mutation<IRadioNodes, ICellArg>({
      query: (args) => ({ url: `${API.ENDPOINTS.GETCELLRN.path}/${args?.deviceKey}/${args?.radioNodeId}`, method: "GET" }),
    }),
    getModelBandsConfig: builder.mutation<IRadioNodeCellConfig, IRnCellArg>({
      query: (args) => ({
        url: `${API.ENDPOINTS.RNCELLDETAILS.path}/${args?.modelKey}`,
        method: "GET",
      }),
    }),
    getAllReports: builder.query<IPaginatedData<IReportsResponse>, IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.ALLREPORTS.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
      }),
      providesTags: [API_SLICE_TAGS.ALLREPORTS],
    }),
    getDownloadReport: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.DOWNLOADREPORT.path}/${args.id}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    getReportPreview: builder.query<IReportPreviewResponse, { id: number; page: number; size: number }>({
      query: (args) => ({
        url: `${API.ENDPOINTS.DOWNLOADREPORT.path}/${args.id}/preview?page=${args.page}&size=${args.size}`,
        method: "GET",
      }),
    }),
    deleteReport: builder.mutation<{ message: string }, { id: number }>({
      query: ({ id }) => ({
        url: `${API.ENDPOINTS.DOWNLOADREPORT.path}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [API_SLICE_TAGS.ALLREPORTS],
    }),

    // EXTERNAL PERFORMANCE 
    getExternalPerformance: builder.mutation<any[], IExternalPerformanceArgs>({
      query: (args) => ({
        // Do not use sanitizeQueryParams — repeated "region" params must be preserved.
        url: `${API.ENDPOINTS.EXTERNALPERFORMANCEDATA.path}/${args?.selectedTab}?${args.filters}`,
        method: "GET",
      }),
    }),

    getNRPerformance: builder.mutation<any[], IExternalPerformanceArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.NRPERFORMANCEDATA.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
      }),
    }),

    getActivityPerformance: builder.mutation<any[], IExternalPerformanceArgs>({
      query: (args) => ({
        // For the activity performance endpoint we expect the caller to build the
        // full query string (including any repeated "region" params). Do not
        // run it through sanitizeQueryParams, otherwise duplicate keys (e.g.
        // multiple regions) would be collapsed.
        url: `${API.ENDPOINTS.ACTIVITYPERFORMANCEDATA.path}/${args?.selectedNetwork}?${args.filters}`,
        method: "GET",
      }),
    }),

    // ADD, DELETE NOTES IN DEVICE DETAILS & CONFIGURATION 
    getAllNotes: builder.query<NoteData[], IArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.NOTES.path}/${args?.deviceKey}`,
        method: "GET",
      }),
    }),

    deleteNote: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.NOTES.path}/${args?.noteId}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    addOrUpdateNote: builder.mutation({
      query: ({ noteId, deviceKey, note }) => {
        const isUpdate = Boolean(noteId);

        return {
          url: isUpdate
            ? `${API.ENDPOINTS.NOTES.path}/${noteId}`   // update
            : `${API.ENDPOINTS.NOTES.path}`,            // create
          method: isUpdate ? "PUT" : "POST",
          body: { deviceKey, note },
        };
      },
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),


    deployment: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.DEPLOYMENT.path}`,
        method: "PUT",
        body: payload,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getLabels: builder.query<Array<string>, void>({
      query: () => ({
        url: API.ENDPOINTS.LABELS.path,
        method: "GET",
      }),
      providesTags: [API_SLICE_TAGS.LABELS],
    }),
    createLabel: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.LABELS.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.LABELS],
    }),
    deleteLabel: builder.mutation({
      query: (labelKey) => ({
        url: `${API.ENDPOINTS.LABELS.path}/${labelKey}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.LABELS],
    }),
    addDeviceLabels: builder.mutation({
      query: (payload: ILabelsToDevice) => ({
        url: `${API.ENDPOINTS.ADDLABELTODEVICE.path}`,
        method: "PUT",
        body: payload,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.LABELS],
    }),
    addLabelDevices: builder.mutation({
      query: (payload: IDevicesToLabel) => ({
        url: `${API.ENDPOINTS.ADDDEVICESTOLABEL.path}`, // From label management 
        method: "PUT",
        body: payload,
        //responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
      invalidatesTags: [API_SLICE_TAGS.LABELS],
    }),
    removeLabelDevices: builder.mutation({
      query: (payload: IDevicesToLabel) => ({
        url: `${API.ENDPOINTS.REMOVELABELFROMDEVICES.path}`,  // From label management 
        method: "PUT",
        body: payload,
        //responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
      invalidatesTags: [API_SLICE_TAGS.LABELS],
    }),
    addProfilesToDevice: builder.mutation({
      query: (payload: IProfilesToDevice) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGPROFILE.path}/${payload.deviceKey}`,
        method: "POST",
        body: payload.profilesKeys,
        //responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),
    removeProfilesFromDevice: builder.mutation({
      query: (payload: IProfilesToDevice) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGPROFILE.path}/${payload.deviceKey}`,
        method: "DELETE",
        body: payload.profilesKeys,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getThresholdCrossingMapProfileData: builder.query<any, IThresholdCrossingArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.THRESHOLDCROSSINGPROFILE.path}/${args?.id}?type=mapped&${sanitizeQueryParams(args.filters)}` }),
    }),
    getThresholdCrossingUnmapProfileData: builder.query<any, IThresholdCrossingArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.THRESHOLDCROSSINGPROFILE.path}/${args?.id}?type=unmapped&${sanitizeQueryParams(args.filters)}` })
    }),

    // THRESHOLD CROSSING
    clearThresholdAlert: builder.mutation<any, number>({
      query: (id) => ({
        url: `v1/performance/alert/${id}/clear`,
        method: "POST",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.THRESHOLDALERT],
    }),
    clearThresholdHistoryBulk: builder.mutation<any, { ids: number[] } | number[]>({
      query: (payload) => {
        const ids = Array.isArray(payload) ? payload : payload.ids;
        return {
          url: "v1/performance/alert/clear-bulk",
          method: "POST",
          body: { ids },
        };
      },
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.THRESHOLDALERT],
    }),
    getThresholdCrossingAllUnmapProfileData: builder.query<any[], IThresholdCrossingArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGPROFILE.path}/unmapped/${args?.id}/all?${sanitizeQueryParams(args.filters)}` })
    }),

    getThresholdAlerts: builder.query<any, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.THRESHOLDALERT.path}?${sanitizeQueryParams(args.filters)}` }),
      providesTags: [API_SLICE_TAGS.THRESHOLDALERT],
    }),
    exportThresholdAlerts: builder.mutation({
      query: (args: IArgs) => ({
        url: `${API.ENDPOINTS.THRESHOLDALERTEXPORT.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),

    // ALARMS OVERVIEW APIS
    getCellsCounts: builder.query<ICellsCounts, string[] | undefined>({
      query: (geoRegions) => {
        const params = new URLSearchParams();
        if (geoRegions && geoRegions.length > 0) {
          params.set('geoRegion', geoRegions.join(','));
        }
        const queryString = params.toString();
        return { url: `${API.ENDPOINTS.CELLSCOUNTS.path}${queryString ? `?${queryString}` : ''}` };
      },
      providesTags: [API_SLICE_TAGS.ALARMS],
    }),

    getAlarmCount: builder.query<IAlarmCount, string[] | undefined>({
      query: (geoRegions) => {
        const params = new URLSearchParams();
        if (geoRegions && geoRegions.length > 0) {
          params.set('geoRegions', geoRegions.join(','));
        }
        const queryString = params.toString();
        return { url: `${API.ENDPOINTS.ALARMCOUNT.path}${queryString ? `?${queryString}` : ''}` };
      },
      providesTags: [API_SLICE_TAGS.ALARMS],
    }),

    topOffenders: builder.query<ITopOffenderResponse, string>({
      query: (queryString) => ({
        url: `${API.ENDPOINTS.TOPOFFENDERS.path}?${queryString}`,
      }),
      providesTags: [API_SLICE_TAGS.ALARMS],
    }),

    exportTopOffenders: builder.mutation({
      query: (queryString: string) => ({
        url: `${API.ENDPOINTS.TOPOFFENDERSEXPORT.path}?${queryString}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),

    // ALARM INCIDENT TRACKING SLICES 

    getAlarmTicket: builder.mutation<any, { alarmId: string }>({
      query: (args) => ({
        url: `${API.ENDPOINTS.ALARMTICKET.path}/${args.alarmId}`,
        method: "GET",
      }),
    }),

    createOrUpdateAlarmTicket: builder.mutation<
      any,
      { alarmId: string; assignee: string; ticketId: string }
    >({
      query: ({ alarmId, assignee, ticketId }) => ({
        url: `${API.ENDPOINTS.ALARMTICKET.path}`,
        method: "PUT",
        body: {
          alarmId,
          assignee,
          ticketId,
        },
      }),
    }),

    deleteAlarmTicket: builder.mutation<any, { alarmId: string }>({
      query: (args) => ({
        url: `${API.ENDPOINTS.ALARMTICKET.path}/${args.alarmId}`,
        method: "DELETE",
      }),
    }),

    getRadioNode: builder.query<IPaginatedData<IEvent>, IArgs>({
      query: (args) => ({
        url: args?.deviceKey
          ? `${API.ENDPOINTS.DISCONNECTEDRADIONODE.path}/${args?.deviceKey}?${sanitizeQueryParams(args.filters)}`
          : `${API.ENDPOINTS.DISCONNECTEDRADIONODE.path}?${sanitizeQueryParams(args.filters)}`,
      }),
      providesTags: [API_SLICE_TAGS.DISCONNECTEDRADIONODE],
    }),

    exportDisconnectedRadioNodes: builder.mutation({
      query: (args: IArgs) => ({
        url: `${API.ENDPOINTS.EXPORTDISCONNECTEDRADIONODES.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),


  }),
});

export const {
  useGetNodesQuery,
  useGetAllDevicesQuery,
  useGetAllDevicesByGroupQuery,
  useGetAlarmsQuery,
  useGetAlarmsByDeviceKeyMutation,
  useGetConditionsQuery,
  useGetEventsQuery,
  useGetSwVersionsQuery,
  useGetInventoryQuery,
  useGetInventoryRadioNodesQuery,
  useGetInventoryCellsQuery,
  useGetCellsSummaryQuery,
  useGetInventoryRadioNodeModelsQuery,
  useGetInventoryStatusQuery,
  useExportInventoryMutation,
  useGetGroupsQuery,
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useRemoveDevicesFromGroupMutation,
  useAddDevicesToGroupMutation,
  useGetDeviceCountsQuery,
  useGetRadioNodesStatsQuery,
  useGetDrawerCountsMutation,
  useExportEventsMutation,
  useExportAlarmsMutation,
  useDeleteDeviceMutation,
  useCreateSiteGroupMutation,
  useDeleteSiteGroupMutation,
  useRemoveDevicesFromSiteGroupMutation,
  useAddDevicesToSiteGroupMutation,
  useGetRnCellConfigQuery,
  useGetRnLteCellConfigMutation,
  useGetRnNrCellConfigMutation,
  useExportConditionsMutation,
  useExportDevicesMutation,
  useGetSupersetTokenQuery,
  useGetLteNrCellRnMutation,
  useGetModelBandsConfigMutation,
  useGetAllReportsQuery,
  useGetDownloadReportMutation,
  useGetReportPreviewQuery,
  useDeleteReportMutation,
  useExportNodeDevicesMutation,
  useGetCellKpiDetailsMutation,
  useGetExternalPerformanceMutation,
  useGetNRPerformanceMutation,
  useGetActivityPerformanceMutation,
  useGetDasRadioNodesQuery,
  useGetDasInventoryQuery,
  useGetDasInventoryGraphQuery,
  useGetSupportedDasModelsQuery,
  useGetDasFeatureFlagsQuery,
  useGetAllNotesQuery,
  useAddOrUpdateNoteMutation,
  useDeleteNoteMutation,
  useDeploymentMutation,
  useGetLabelsQuery,
  useCreateLabelMutation,
  useDeleteLabelMutation,
  useGetNodesCountsQuery,
  useGetAlarmTicketMutation,
  useCreateOrUpdateAlarmTicketMutation,
  useDeleteAlarmTicketMutation,
  useAddDeviceLabelsMutation,
  useAddLabelDevicesMutation,
  useRemoveLabelDevicesMutation,
  useAddProfilesToDeviceMutation,
  useRemoveProfilesFromDeviceMutation,
  useGetThresholdCrossingMapProfileDataQuery,
  useGetThresholdCrossingUnmapProfileDataQuery,
  useGetThresholdCrossingAllUnmapProfileDataQuery,
  useClearThresholdAlertMutation,
  useClearThresholdHistoryBulkMutation,
  useGetThresholdAlertsQuery,
  useExportThresholdAlertsMutation,
  useGetCellsCountsQuery,
  useGetAlarmCountQuery,
  useTopOffendersQuery,
  useExportTopOffendersMutation,
  useGetRadioNodeQuery,
  useExportDisconnectedRadioNodesMutation,
} = deviceApiSlice;
